"use client";

import React from "react";
import { Card } from "@/components/ui/card";
import { Loader2, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import CartCoverageList from "./CartCoverageList";
import { OfferResponse } from "@/types/simpego-api";
import { BaseCoverageId } from "@/config/coverages";
import { isCoverageStateValid } from "@/utils/coveragePayload";

interface PlanSummaryProps {
  currentOffer: OfferResponse | null;
  petBirthDate?: Date | null;
  currentFinalPrice: number | null;
  paymentInterval: 1 | 2 | 4 | 12;
  isLoading: boolean;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  declinedCoverages?: Partial<Record<BaseCoverageId, string>>;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  annualDeductible: number | null;
  underwritingFailed?: boolean;
  underwritingReason?: string | null;
}

const PlanSummary: React.FC<PlanSummaryProps> = ({
  currentOffer,
  petBirthDate,
  currentFinalPrice,
  paymentInterval,
  isLoading,
  coverageSelections,
  declinedCoverages,
  subCoverageSelections,
  subCoverageSumInsured,
  coverageDeductibles,
  coverageSumInsured,
  coverageSumInsuredPerCase,
  emergencyCareSumInsuredPerDay,
  annualDeductible,
  underwritingFailed = false,
  underwritingReason = null,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const getPaymentFrequencyText = (interval: 1 | 2 | 4 | 12) => {
    return t(`common.paymentIntervalOptions.${interval}`);
  };

  const monthlyPrice = currentFinalPrice !== null ? (currentFinalPrice * paymentInterval) / 12 : null;

  // An invalid coverage input (e.g. deductible >= sum insured, an out-of-range sum) is never sent to the
  // pricing API, so no price will ever arrive — say so instead of showing a spinner forever.
  const isCalculationPossible = isCoverageStateValid({
    coverageSelections,
    subCoverageSelections,
    subCoverageSumInsured,
    coverageDeductibles,
    coverageSumInsured,
    coverageSumInsuredPerCase,
    emergencyCareSumInsuredPerDay,
    annualDeductible,
  });

  const underwritingMessage = underwritingReason || t("underwritingError.defaultReason");

  return (
    <div className="w-full mb-8">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="overflow-hidden sticky top-2 z-20">
          <CollapsibleTrigger asChild>
            <div className="p-6 cursor-pointer hover:bg-accent/50">
              <div className="flex justify-between items-start">
                <div className="flex-1 flex flex-col items-center text-center">
                  <div className="text-center">
                    {underwritingFailed ? (
                      <p className="text-sm font-semibold text-destructive">
                        {underwritingMessage}
                      </p>
                    ) : !isCalculationPossible ? (
                      <p className="text-sm font-semibold text-destructive">{t("common.calculationNotPossible")}</p>
                    ) : isLoading || monthlyPrice === null ? (
                      // A missing price (isLoading false but monthlyPrice still null, e.g. the
                      // query hasn't started yet) means we're still waiting on a real figure —
                      // never show a blank "-" placeholder, always a spinner instead.
                      <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-destructive">
                          CHF {monthlyPrice.toFixed(2)} / {t("common.monthly")}
                        </p>
                        {paymentInterval !== 12 && currentFinalPrice !== null && (
                          <p className="text-sm text-muted-foreground">
                            CHF {currentFinalPrice.toFixed(2)} / {getPaymentFrequencyText(paymentInterval)}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={cn("h-6 w-6 ml-4 transition-transform shrink-0", isOpen && "rotate-180")}
                />
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="max-h-[70vh] overflow-y-auto">
            {/* A rejected offer is never listed as a fallback: the message above already says why. */}
            {!underwritingFailed && (
              <CartCoverageList
                offer={currentOffer}
                coverageSelections={coverageSelections}
                declinedCoverages={declinedCoverages}
                petBirthDate={petBirthDate}
              />
            )}
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default PlanSummary;
